/**
 * Post-bootstrap user hydration — fills AuthSnapshot.user from
 * GET /api/v1/employees/me. Tolerates 404 (HR admins without a linked
 * employee row, per backend be-030) and network errors. NEVER throws —
 * this can run pre-router, where there is no error boundary or toast
 * provider in scope.
 *
 * Resolved displayName fallback chain:
 *   1. /me response: locale-preferred full name (`fullNameEn` / `fullNameAr`)
 *      with the other-locale name as secondary
 *   2. JWT `name` claim if the IdP includes one (defensive — backend's
 *      current claims do not, but readers shouldn't fail closed)
 *   3. email local-part
 *   4. raw email
 *
 * Idempotent — re-runs with an unchanged result are a no-op (the AuthStore
 * Object.is gate plus a shallow user-field compare here both protect against
 * unnecessary re-renders).
 *
 * Resolves to what it learned about the record, so the caller can hand the
 * answer to every other reader of `/employees/me` instead of each asking again.
 * A login found to have no employee record is remembered briefly for the tab
 * (`noEmployeeMarker`: per user, short-lived, cleared by a sign-in, a sign-out
 * or any 200), and loads in that window don't ask: the 404 was a request on
 * every page load.
 *
 * @see project conventions "EMP_NOT_FOUND on /leaves/calendar" entry — same backend
 *      shape applies on /employees/me for HR admins without an employee row.
 */
import { decodeJwtClaims } from "../tokens/jwt.js";
import { noEmployeeMarker } from "../tokens/noEmployeeMarker.js";
import type { AuthStore } from "../react/AuthProvider.js";
import type { AuthUser } from "../types.js";

export interface HydrateUserOpts {
  apiBaseUrl: string;
  accessToken: string;
  /** Best-effort hint when the snapshot doesn't already carry an email. Optional. */
  email?: string | null;
  locale: "en" | "ar";
  store: AuthStore;
  /** Override fetch for tests. */
  fetcher?: typeof fetch;
}

/** What the bootstrap knows about the caller's employee record. */
export type MeLookup =
  /** The `/employees/me` body. */
  | { kind: "found"; employee: unknown }
  /** A login with no employee record (an admin setting a company up). */
  | { kind: "none" }
  /** Not asked, or no definite answer. */
  | { kind: "unknown" };

interface MeResponse {
  id?: string;
  fullNameEn?: string;
  fullNameAr?: string;
  email?: string | null;
}

function resolveDisplayName(
  me: MeResponse | null,
  jwtName: string | null,
  email: string | null,
  locale: "en" | "ar",
): string {
  if (me) {
    const preferred = locale === "ar" ? me.fullNameAr : me.fullNameEn;
    const fallback = locale === "ar" ? me.fullNameEn : me.fullNameAr;
    if (preferred !== undefined && preferred.trim() !== "") return preferred.trim();
    if (fallback !== undefined && fallback.trim() !== "") return fallback.trim();
  }
  if (jwtName !== null && jwtName.trim() !== "") return jwtName.trim();
  if (email !== null && email.includes("@")) {
    const local = email.split("@")[0];
    if (local !== undefined && local !== "") return local;
  }
  return email ?? "";
}

export async function hydrateUserFromMe(opts: HydrateUserOpts): Promise<MeLookup> {
  const { apiBaseUrl, accessToken, email, locale, store, fetcher } = opts;
  const snap = store.getSnapshot();
  if (!snap.isAuthenticated) return { kind: "unknown" }; // bootstrap landed unauth
  const fetchImpl = fetcher ?? fetch;

  // Best-effort JWT name claim (some IdPs include it; ours may not — defensive).
  const claims = decodeJwtClaims(accessToken) as
    | (ReturnType<typeof decodeJwtClaims> & { name?: string })
    | null;
  const jwtName = claims?.name ?? null;
  // The hint holds the email, or the username for someone who has none.
  const hint = email ?? snap.user?.email ?? null;
  const fallbackEmail = hint !== null && hint.includes("@") ? hint : null;
  const username =
    snap.user?.username ?? (hint !== null && hint !== "" && !hint.includes("@") ? hint : null);

  let me: MeResponse | null = null;
  let userId = snap.user?.id ?? claims?.sub ?? "";
  let resolvedEmail = fallbackEmail ?? "";
  // The JWT subject: stable across loads, unlike `user.id` (the employee id once found).
  const subject = snap.userId ?? claims?.sub ?? "";
  let lookup: MeLookup = { kind: "unknown" };

  try {
    // Already known to have no record: don't ask again.
    const res = noEmployeeMarker.holds(subject)
      ? null
      : await fetchImpl(apiBaseUrl + "/api/v1/employees/me", {
          method: "GET",
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        });
    if (res === null) {
      lookup = { kind: "none" };
    } else if (res.ok) {
      me = (await res.json().catch(() => null)) as MeResponse | null;
      if (me !== null) {
        lookup = { kind: "found", employee: me };
        noEmployeeMarker.clear();
      }
      if (me?.id !== undefined && me.id !== "") userId = me.id;
      // null for an employee who signs in with a username.
      if (typeof me?.email === "string" && me.email !== "") resolvedEmail = me.email;
    } else if (res.status === 404) {
      lookup = { kind: "none" };
      noEmployeeMarker.set(subject);
    } else {
      // Non-404 error — log once, fall through to JWT/email fallback.

      console.warn(
        "[auth] hydrateUserFromMe: /employees/me returned",
        res.status,
        "correlationId=",
        res.headers.get("x-correlation-id"),
      );
    }
  } catch (err) {
    console.warn("[auth] hydrateUserFromMe: network error", err);
  }

  const displayName = resolveDisplayName(me, jwtName, resolvedEmail, locale) || (username ?? "");
  const next: AuthUser = {
    id: userId,
    email: resolvedEmail,
    displayName,
    username,
  };

  // Skip pointless re-renders when nothing changed.
  const cur = snap.user;
  if (
    cur !== null &&
    cur.id === next.id &&
    cur.email === next.email &&
    cur.displayName === next.displayName &&
    (cur.username ?? null) === next.username
  ) {
    return lookup;
  }
  store.set({ ...store.getSnapshot(), user: next });
  return lookup;
}
